// ===========================================
// Family Credit Panel (#642)
// ===========================================
// The family's account credit on the admin family page: what is held, what is flagged for
// refund, what is available, and every movement (Added / Applied / Refund due / Refunded,
// voided ones struck through). Shown with operations_invoices.read; the refund actions need
// operations_refunds.write. WHO made a movement is the owner's only (the server sends null).

import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { usePermissions } from '../../../hooks/usePermissions';
import { useFamilyCredit, useFlagRefundDue, useVoidRefundEntry } from '../../../hooks/useFamilyCredit';
import type { FamilyCreditEntry, FamilyCreditKind } from '../../../api/familyCredit.api';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatCurrencyFromDollars } from '../../../utils/currency';
import MarkRefundedDialog from './MarkRefundedDialog';

const KIND_LABELS: Record<FamilyCreditKind, string> = {
  ADDED: 'Added',
  APPLIED: 'Applied',
  REFUND_DUE: 'Refund due',
  REFUNDED: 'Refunded',
};

/** A @db.Date key (YYYY-MM-DD) as dd/mm/yyyy, without a Date object (no zone can shift it). */
function dayKeyToAu(key: string): string {
  return key.split('-').reverse().join('/');
}

const money = (v: string) => formatCurrencyFromDollars(parseFloat(v));

export default function FamilyCreditPanel({ familyId }: { familyId: string | undefined }) {
  const { can } = usePermissions();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const canRead = can('operations_invoices');
  const mayRefund = can('operations_refunds', 'write');
  const { data, isLoading, isError, refetch } = useFamilyCredit(familyId, canRead);

  const [refundDueOpen, setRefundDueOpen] = useState(false);
  const [toRefund, setToRefund] = useState<FamilyCreditEntry | null>(null);
  const [toVoid, setToVoid] = useState<FamilyCreditEntry | null>(null);

  if (!canRead || !familyId) return null;

  const available = data ? parseFloat(data.available) : 0;

  return (
    <Card variant="outlined" sx={{ mt: 3 }} data-testid="family-credit-panel">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AccountBalanceWalletIcon color="primary" />
          <Typography variant="h6">Account credit</Typography>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError || !data ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Could not load this family&apos;s account credit.
          </Alert>
        ) : data.entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No account credit.
          </Typography>
        ) : (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }} sx={{ mb: 1 }}>
              <Typography variant="body2">
                Balance: <strong>{money(data.balance)}</strong>
              </Typography>
              <Typography variant="body2">
                Refund due: <strong>{money(data.refundDue)}</strong>
              </Typography>
              <Typography variant="body2">
                Available: <strong>{money(data.available)}</strong>
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Held credit comes off the family&apos;s next invoice when it is sent.
            </Typography>
            {mayRefund && available > 0 && (
              <Button size="small" variant="outlined" sx={{ mb: 2 }} onClick={() => setRefundDueOpen(true)}>
                Mark as Refund due
              </Button>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>What</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Invoice</TableCell>
                    {mayRefund && <TableCell />}
                  </TableRow>
                </TableHead>
                {/* One <tbody> per movement: its figures on one line that never wraps, then the note,
                    reference, void reason and who on a full-width line underneath (Andrew
                    30/10/2026: a Note column wrapped one word per line). */}
                {data.entries.map((e) => {
                  const voided = !!e.voidedAt;
                  const hasDetail = !!(e.note || e.refundReference || (voided && e.voidReason) || e.createdByName);
                  const top = { whiteSpace: 'nowrap', ...(hasDetail ? { borderBottom: 'none', pb: 0 } : {}) } as const;
                  return (
                    <TableBody key={e.id} data-testid={voided ? 'credit-entry-voided' : 'credit-entry'}>
                      <TableRow>
                        <TableCell sx={top}>{dayKeyToAu(e.occurredOn)}</TableCell>
                        <TableCell sx={{ ...top, textDecoration: voided ? 'line-through' : 'none' }}>
                          {KIND_LABELS[e.kind]}
                          {voided && ' (voided)'}
                          {e.kind === 'REFUND_DUE' && !voided && !e.open && ' (refunded)'}
                        </TableCell>
                        <TableCell align="right" sx={{ ...top, textDecoration: voided ? 'line-through' : 'none' }}>
                          {money(e.amount)}
                        </TableCell>
                        <TableCell sx={top}>
                          {e.invoice ? (
                            <Link component={RouterLink} to={`/${schoolSlug}/admin/invoices/${e.invoice.id}`}>
                              {e.invoice.invoiceNumber}
                            </Link>
                          ) : (
                            ''
                          )}
                        </TableCell>
                        {mayRefund && (
                          <TableCell align="right" sx={top}>
                            {!voided && e.kind === 'REFUND_DUE' && e.open && (
                              <>
                                <Button size="small" onClick={() => setToRefund(e)}>
                                  Mark refunded
                                </Button>
                                <Button size="small" onClick={() => setToVoid(e)}>
                                  Back to credit
                                </Button>
                              </>
                            )}
                            {!voided && e.kind === 'REFUNDED' && (
                              <Button size="small" color="error" onClick={() => setToVoid(e)}>
                                Void refund record
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      {hasDetail && (
                        <TableRow>
                          <TableCell colSpan={mayRefund ? 5 : 4} sx={{ pt: 0.5, overflowWrap: 'break-word' }}>
                            {(e.note || e.refundReference) && (
                              <Typography variant="body2" sx={{ display: 'block' }}>
                                {e.note}
                                {e.refundReference && `${e.note ? ' · ' : ''}Ref: ${e.refundReference}`}
                              </Typography>
                            )}
                            {voided && e.voidReason && (
                              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                Voided: {e.voidReason}
                                {e.voidedByName ? ` (${e.voidedByName})` : ''}
                              </Typography>
                            )}
                            {e.createdByName && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                By {e.createdByName}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  );
                })}
              </Table>
            </Box>
          </>
        )}
      </CardContent>

      <RefundDueDialog
        open={refundDueOpen}
        onClose={() => setRefundDueOpen(false)}
        familyId={familyId}
        available={available}
      />
      <MarkRefundedDialog open={!!toRefund} onClose={() => setToRefund(null)} due={toRefund} />
      <VoidRefundEntryDialog entry={toVoid} onClose={() => setToVoid(null)} />
    </Card>
  );
}

/** Held credit → Refund due: part or all of the available credit, with a note (D12). */
function RefundDueDialog({
  open,
  onClose,
  familyId,
  available,
}: {
  open: boolean;
  onClose: () => void;
  familyId: string;
  available: number;
}) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const mutation = useFlagRefundDue();
  const cents = Math.round((parseFloat(amount) || 0) * 100);
  const valid = cents > 0 && cents <= Math.round(available * 100) && note.trim().length >= 3;

  const close = () => {
    setAmount('');
    setNote('');
    mutation.reset();
    onClose();
  };

  const confirm = async () => {
    try {
      await mutation.mutateAsync({ familyId, amount: cents / 100, note: note.trim() });
      close();
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>Mark as Refund due</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {formatCurrencyFromDollars(available)} available. Flag part or all of it to be paid back.
        </Typography>
        <Button size="small" sx={{ mb: 1 }} onClick={() => setAmount(available.toFixed(2))}>
          All of it
        </Button>
        <TextField
          fullWidth
          required
          type="number"
          label="Amount to refund"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: 0.01, max: available }}
          error={amount !== '' && (cents <= 0 || cents > Math.round(available * 100))}
          helperText={amount !== '' && cents > Math.round(available * 100) ? 'More than the available credit' : undefined}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          required
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText="For example: the family asked for the money back"
        />
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(mutation.error, 'Could not flag the refund')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={confirm} disabled={!valid || mutation.isPending}>
          Mark as Refund due
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "Back to credit" on an open Refund due, or "Void refund record" on a Refunded. Reason required. */
function VoidRefundEntryDialog({ entry, onClose }: { entry: FamilyCreditEntry | null; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const mutation = useVoidRefundEntry();
  const backToCredit = entry?.kind === 'REFUND_DUE';

  const close = () => {
    setReason('');
    mutation.reset();
    onClose();
  };

  const confirm = async () => {
    if (!entry) return;
    try {
      await mutation.mutateAsync({ entryId: entry.id, reason: reason.trim() });
      close();
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open={!!entry} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>{backToCredit ? 'Back to credit' : 'Void refund record'}</DialogTitle>
      <DialogContent>
        {entry && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {backToCredit
              ? `${formatCurrencyFromDollars(parseFloat(entry.amount))} is held as credit again. It comes off any invoice the family has open now, oldest first.`
              : `The ${formatCurrencyFromDollars(parseFloat(entry.amount))} refund record is voided and the refund is due again.`}
          </Typography>
        )}
        <TextField
          fullWidth
          required
          autoFocus
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 500 }}
        />
        {mutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {apiErrorMessage(mutation.error, 'Could not void the refund record')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={backToCredit ? 'primary' : 'error'}
          onClick={confirm}
          disabled={reason.trim().length < 3 || mutation.isPending}
        >
          {backToCredit ? 'Back to credit' : 'Void refund record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
